import { useQuery } from '@apollo/client';
import { RemoveCircle, Router } from '@mui/icons-material';
import { Box, Chip, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { useRemoveDevicesFromScheduledShow } from '~/api/scheduled-show';
import { ReadOnlyDataGrid, hiddenHeader } from '~/components/data-grid';
import { DeviceLogo, DeviceStatus } from '~/components/devices';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { Link } from '~/components/link';
import { AuthorizedLink } from '~/components/links';
import { ListRowHeadingValue } from '~/components/list-row';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import { MetaItem, SettingsBody, SettingsData, SettingsHead } from '~/components/settings';
import { TableAction } from '~/components/table';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { assert } from '~/lib/assert';
import { formatDateOnly } from '~/lib/datetime';
import { getScheduleDescription } from '~/lib/schedules';
import { ScheduledShowGetDocument } from '../queries/queries.generated';
import { ScheduleShowToolbar } from './components/toolbar';

export const ScheduleShowDetails = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const link = useLink();

  const theme = useTheme();
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  const params = useParams<{ scheduledShowId: string }>();

  const scheduledShowId = parseInt(params.scheduledShowId ?? '');

  const [removeDevicesFromScheduledShow] = useRemoveDevicesFromScheduledShow();

  const { data, loading } = useQuery(ScheduledShowGetDocument, {
    variables: { networkId: currentNetwork.id, scheduledShowId },
  });

  const scheduledShow = data?.network?.scheduledShow;

  const devices = scheduledShow?.devices;

  const removeDevice = useCallback(
    async (deviceId: number) => {
      assert(scheduledShow != null);
      if (!(await confirmDelete())) return;
      await removeDevicesFromScheduledShow({
        variables: { input: { deviceIds: [deviceId], scheduledShowId: scheduledShow.id } },
      });
    },
    [confirmDelete, removeDevicesFromScheduledShow, scheduledShow],
  );

  if (!loading && !data?.network?.scheduledShow)
    return <Navigate to={link('/not-found')} replace />;

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      {scheduledShow && (
        <>
          <ScheduleShowToolbar scheduledShow={scheduledShow} />
          <PageContainer>
            <Box>
              <SettingsHead>{t('common.labels.details')}</SettingsHead>
              <SettingsBody>
                <SettingsData className="last no-space-between">
                  <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                    <MetaItem className="first">
                      <ListRowHeadingValue
                        heading={t('pages.schedules.detail.fields.name')}
                        value={scheduledShow.name}
                      />
                    </MetaItem>
                    <MetaItem>
                      <ListRowHeadingValue
                        heading={t('pages.schedules.detail.fields.show')}
                        value={
                          <Link
                            to={link(
                              `/shows/${scheduledShow.show.showFolderId}/show/${scheduledShow.show.id}/design`,
                            )}
                          >
                            {scheduledShow.show.name}
                          </Link>
                        }
                      />
                    </MetaItem>
                    <MetaItem>
                      <ListRowHeadingValue
                        heading={t('common.labels.devices')}
                        value={<Chip color="primary" label={scheduledShow.deviceCount || 0} />}
                      />
                    </MetaItem>
                    <MetaItem>
                      <ListRowHeadingValue
                        heading={t('pages.schedules.detail.fields.start_date')}
                        value={
                          scheduledShow.schedule?.startDate
                            ? formatDateOnly(scheduledShow.schedule.startDate, {
                                zone: 'UTC',
                              })
                            : ''
                        }
                      />
                    </MetaItem>
                    <MetaItem>
                      <ListRowHeadingValue
                        heading={t('pages.schedules.detail.fields.schedule_field')}
                        value={
                          scheduledShow.schedule
                            ? getScheduleDescription(scheduledShow.schedule, t)
                            : ''
                        }
                      />
                    </MetaItem>
                    <MetaItem>
                      <ListRowHeadingValue
                        heading={t('common.labels.created')}
                        value={formatDateOnly(scheduledShow.createdAt)}
                      />
                    </MetaItem>
                    <MetaItem className="last">
                      <ListRowHeadingValue
                        heading={t('pages.schedules.detail.fields.updated')}
                        value={formatDateOnly(scheduledShow.updatedAt)}
                      />
                    </MetaItem>
                  </Box>
                </SettingsData>
              </SettingsBody>
            </Box>
            <Paper sx={{ mt: 2 }}>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: theme.spacing(2),
                }}
              >
                <Box sx={{ alignItems: 'center', display: 'flex', flex: 1, gap: 2 }}>
                  <Box>
                    <Router sx={{ fontSize: '4em' }} color="primary" />
                  </Box>
                  <Box>
                    <Typography variant="h4">{t('common.labels.devices')}</Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      {t('common.labels.devices_description')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  backgroundColor: 'background.default',
                  borderTop: '1px solid',
                  borderColor: theme.palette.borders.dark,
                  padding: theme.spacing(2),
                }}
              >
                <ReadOnlyDataGrid
                  autoHeight
                  columns={[
                    {
                      align: 'center',
                      display: 'flex',
                      headerAlign: 'center',
                      field: 'kind',
                      headerName: t('common.labels.type'),
                      renderCell: ({ row }) => <DeviceLogo kind={row.kind} />,
                      sortable: false,
                      width: 50,
                    },
                    {
                      field: 'id',
                      headerName: t('pages.schedules.detail.devices_table.columns.id'),
                      sortable: false,
                      width: 65,
                    },
                    {
                      align: 'center',
                      headerAlign: 'center',
                      field: 'status',
                      ...hiddenHeader(t('common.labels.status')),
                      renderCell: ({ row }) => <DeviceStatus device={row} />,
                      sortable: false,
                      width: 65,
                    },
                    {
                      field: 'name',
                      flex: 1,
                      headerName: t('common.labels.name'),
                      minWidth: isSmallAndUp ? 180 : 0,
                      renderCell: ({ row }) => (
                        <AuthorizedLink authorized={row.canUpdate.value} entity={row} />
                      ),
                      sortable: false,
                    },
                    {
                      field: 'group',
                      flex: 1,
                      headerName: t('common.labels.group'),
                      minWidth: 120,
                      sortable: false,
                      renderCell: ({ row }) => (
                        <AuthorizedLink
                          authorized={row.group?.canUpdate.value}
                          entity={row.group}
                        />
                      ),
                    },
                    {
                      field: 'ipAddress',
                      flex: 1,
                      headerName: t('common.labels.ip_address'),
                      minWidth: 120,
                      sortable: false,
                      valueGetter: (_value, row) => row.ipAddress,
                    },
                    {
                      field: 'playerVersion',
                      flex: 1,
                      headerName: t('common.labels.version'),
                      minWidth: 100,
                      sortable: false,
                      valueGetter: (_value, row) => row.playerVersion || '-',
                    },
                    {
                      align: 'right',
                      field: 'actions',
                      getActions: ({ row }) => [
                        <TableAction
                          color="error"
                          disabled={!row.canUpdate.value}
                          key={row.id}
                          title={
                            row.canUpdate.value
                              ? t('common.actions.remove')
                              : row.canUpdate.reasons?.fullMessages[0] ?? ''
                          }
                          Icon={RemoveCircle}
                          onClick={() => removeDevice(Number(row.id))}
                        />,
                      ],
                      sortable: false,
                      type: 'actions',
                    },
                  ]}
                  rows={devices ?? []}
                  slots={{
                    columnResizeIcon: () => null,
                    noRowsOverlay: () => {
                      return (
                        <Box
                          height="100%"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          {t('pages.schedules.detail.devices_table.empty_state')}
                        </Box>
                      );
                    },
                  }}
                />
              </Box>
            </Paper>
          </PageContainer>
          <ConfirmDialog
            {...confirmDeleteProps}
            confirm={t('common.dialogs.remove')}
            cancel={t('common.dialogs.cancel')}
            deleteConfirm
            prompt={t('pages.schedules.detail.remove_device_dialog.prompt')}
            title={t('pages.schedules.detail.remove_device_dialog.title')}
          />
        </>
      )}
    </LoadingPane>
  );
};
